"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, Cable, Fan, FileText, OctagonAlert, ShieldCheck, SlidersHorizontal, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface ChecklistItem {
  id: string;
  tag: string;
  title: string;
  description: string;
  iconName: "wrench" | "controller" | "inflatable" | "blower" | "cables" | "emergency";
}
const CHECKLIST_ITEMS: ChecklistItem[] = [{
  id: "item-1",
  tag: "01 / SPEC",
  title: "Bull & Base",
  description: "Look for obvious damage, loose components or anything that appears different from the previous setup.",
  iconName: "wrench"
}, {
  id: "item-2",
  tag: "02 / SPEC",
  title: "Controller",
  description: "Check the visible condition of the controller, cables and connections.",
  iconName: "controller"
}, {
  id: "item-3",
  tag: "03 / SPEC",
  title: "Inflatable",
  description: "Look for obvious damage, deflation or areas that need attention.",
  iconName: "inflatable"
}, {
  id: "item-4",
  tag: "04 / SPEC",
  title: "Blower",
  description: "Confirm that the blower is operating as required for the inflatable.",
  iconName: "blower"
}, {
  id: "item-5",
  tag: "05 / SPEC",
  title: "Cables & Connections",
  description: "Check for obvious damage and secure connections where applicable.",
  iconName: "cables"
}, {
  id: "item-6",
  tag: "06 / SPEC",
  title: "Emergency Controls",
  description: "Understand and check the machine's emergency controls according to its instructions before operation.",
  iconName: "emergency"
}];
function ChecklistIcon({
  name
}: {
  name: ChecklistItem["iconName"];
}) {
  switch (name) {
    case "wrench":
      return <Wrench className="size-5 text-primary" />;
    case "controller":
      return <SlidersHorizontal className="size-5 text-primary" />;
    case "inflatable":
      return <ShieldCheck className="size-5 text-primary" />;
    case "blower":
      return <Fan className="size-5 text-primary" />;
    case "cables":
      return <Cable className="size-5 text-primary" />;
    case "emergency":
      return <OctagonAlert className="size-5 text-primary" />;
    default:
      return <Wrench className="size-5 text-primary" />;
  }
}
export default function Checklist() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="preoperationchecklist" data-nav="light" className="relative w-full border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="border-b border-border pb-8 md:pb-12">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="size-2 bg-primary" />
              <p className="font-mono text-xs font-semibold tracking-widest text-muted-foreground uppercase">
                Field Procedure // Pre-Session
              </p>
            </div>
            <Badge variant="outline" className="rounded-none border-border bg-muted/50 font-mono text-xs uppercase text-muted-foreground">
              Operator Protocol
            </Badge>
          </div>

          <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight uppercase sm:text-4xl md:text-5xl">
            Check the Equipment Before Each Operating Session
          </h2>

          <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            A simple pre-use review can help identify visible problems before operation. The exact inspection and maintenance procedure should always follow the documentation for the machine you own.
          </p>
        </div>

        {/* 6-Card Checklist Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="mt-8 grid grid-cols-1 gap-px bg-border sm:grid-cols-2 lg:grid-cols-3">
          {CHECKLIST_ITEMS.map((item, i) => <motion.article key={item.id} data-index={i} variants={itemVariants} className="group relative flex flex-col justify-between bg-card p-6 transition-[border-color,background-color] duration-200 hover:bg-muted/40 sm:p-8">
              <div>
                {/* Top bar with index tag and technical icon */}
                <div className="flex items-center justify-between border-b border-border/80 pb-4">
                  <span className="font-mono text-xs font-semibold tracking-wider text-muted-foreground group-hover:text-primary">
                    {item.tag}
                  </span>
                  <div className="flex size-9 items-center justify-center border border-border bg-muted/40 transition-colors group-hover:border-primary/50 group-hover:bg-card">
                    <ChecklistIcon name={item.iconName} />
                  </div>
                </div>

                {/* Content */}
                <h3 className="mt-6 font-serif text-xl font-bold uppercase tracking-tight text-card-foreground">
                  {item.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {item.description}
                </p>
              </div>

              {/* Bottom indicator status */}
              <div className="mt-8 flex items-center gap-2 pt-4 border-t border-border/50 text-xs font-mono text-muted-foreground">
                <span className="size-1.5 rounded-full bg-border group-hover:bg-primary transition-colors" />
                <span>VERIFY PRIOR TO POWER ON</span>
              </div>
            </motion.article>)}
        </motion.div>

        {/* Closing Advisory Callout */}
        <div className="mt-8 border-l-4 border-primary bg-card p-6 shadow-xs sm:p-8 border-t border-r border-b border-border">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div className="mt-0.5 flex size-10 shrink-0 items-center justify-center bg-primary/10 text-primary">
                <AlertTriangle className="size-5" />
              </div>
              <div className="space-y-1">
                <p className="font-mono text-xs font-semibold tracking-wider text-primary uppercase">
                  Safety Precaution // Operator Directive
                </p>
                <p className="text-sm font-medium leading-relaxed text-card-foreground sm:text-base">
                  If something appears damaged or operates differently from expected, stop and investigate rather than continuing to use the equipment.
                </p>
              </div>
            </div>

            <div className="shrink-0 pt-2 md:pt-0">
              <Button asChild variant="outline" className="w-full rounded-none border-border font-mono text-xs uppercase tracking-wider md:w-auto">
                <Link to="/SetupSafetyAndMaintenancePage">
                  <FileText className="mr-2 size-4" />
                  View Setup Specs
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}